import { useEffect, useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tabs,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CalendarOutlined,
  DeleteOutlined,
  ImportOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Toast } from '@/components/Toast'
import holidayApi from './api'
import {
  HolidayDayType,
  HolidayImportPayload,
  HolidayImportPreviewItem,
  HolidayItem,
} from './types'

const { RangePicker } = DatePicker
const { TextArea } = Input
const { Text } = Typography

const DAY_TYPE_OPTIONS: Array<{ label: string; value: HolidayDayType }> = [
  { label: '假期', value: 'HOLIDAY' },
  { label: '补班', value: 'MAKEUP_DAY' },
]

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六']

const MONTH_NAMES = [
  '1月',
  '2月',
  '3月',
  '4月',
  '5月',
  '6月',
  '7月',
  '8月',
  '9月',
  '10月',
  '11月',
  '12月',
]

type AddHolidayFormValues = {
  dateRange: [Dayjs, Dayjs]
  name: string
  dayType: HolidayDayType
}

type EditHolidayFormValues = {
  holidayDate: Dayjs
  name: string
  dayType: HolidayDayType
}

function padDate(date: Dayjs) {
  return date.format('YYYY-MM-DD')
}

function sortItems(items: HolidayItem[]) {
  return [...items].sort((left, right) => left.holidayDate.localeCompare(right.holidayDate))
}

function toDisplayDate(value: string) {
  return dayjs(value).format('YYYY-MM-DD')
}

function getDayTypeColor(dayType: HolidayDayType) {
  return dayType === 'HOLIDAY' ? 'red' : 'green'
}

function getDayTypeText(dayType: HolidayDayType) {
  return dayType === 'HOLIDAY' ? '假期' : '补班'
}

function getMonthItems(items: HolidayItem[], monthIndex: number) {
  return items.filter((item) => dayjs(item.holidayDate).month() === monthIndex)
}

function buildHolidayMap(items: HolidayItem[]) {
  return new Map(items.map((item) => [item.holidayDate, item]))
}

function buildMonthCells(year: number, monthIndex: number, items: HolidayItem[]) {
  const monthStart = dayjs().year(year).month(monthIndex).startOf('month')
  const daysInMonth = monthStart.daysInMonth()
  const leadingEmptyCells = monthStart.day()
  const holidayMap = buildHolidayMap(items)
  const cells: Array<{ date: Dayjs | null; item: HolidayItem | null }> = []

  for (let index = 0; index < leadingEmptyCells; index += 1) {
    cells.push({ date: null, item: null })
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = monthStart.date(day)
    cells.push({
      date,
      item: holidayMap.get(padDate(date)) ?? null,
    })
  }

  while (cells.length % 7 !== 0) {
    cells.push({ date: null, item: null })
  }

  const rows: Array<Array<{ date: Dayjs | null; item: HolidayItem | null }>> = []
  for (let index = 0; index < cells.length; index += 7) {
    rows.push(cells.slice(index, index + 7))
  }

  return rows
}

function buildCalendarColumns(
  onEdit: (item: HolidayItem) => void,
  onDelete: (item: HolidayItem) => void,
): ColumnsType<HolidayItem> {
  return [
    {
      title: '日期',
      dataIndex: 'holidayDate',
      width: 140,
      render: (value: string) => toDisplayDate(value),
    },
    {
      title: '节假日名称',
      dataIndex: 'name',
      render: (value: string) => <span className="font-medium text-slate-900">{value}</span>,
    },
    {
      title: '类型',
      dataIndex: 'dayType',
      width: 110,
      render: (value: HolidayDayType) => <Tag color={getDayTypeColor(value)}>{getDayTypeText(value)}</Tag>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: unknown, record) => (
        <Space size={0}>
          <Button type="link" onClick={() => onEdit(record)}>
            编辑
          </Button>
          <Button type="link" danger onClick={() => onDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]
}

function MonthCalendarCard({
  year,
  monthIndex,
  items,
  compact = false,
  onAddDate,
  onEditItem,
}: {
  year: number
  monthIndex: number
  items: HolidayItem[]
  compact?: boolean
  onAddDate?: (date: Dayjs) => void
  onEditItem?: (item: HolidayItem) => void
}) {
  const monthCells = buildMonthCells(year, monthIndex, items)
  const monthCount = items.length

  return (
    <Card
      size="small"
      className={compact ? 'h-full min-h-[310px]' : 'h-full min-h-[360px]'}
      title={<span className="text-[15px] font-semibold text-slate-900">{MONTH_NAMES[monthIndex]}</span>}
      extra={<Tag className="m-0 rounded-full">{monthCount} 条</Tag>}
    >
      <div className="space-y-2">
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-slate-500">
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className="py-1">
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {monthCells.flatMap((week) =>
            week.map((cell, index) => {
              if (!cell.date) {
                return (
                  <div
                    key={`blank-${index}`}
                    className={`rounded-xl border border-dashed border-slate-100 bg-slate-50/40 ${
                      compact ? 'h-12' : 'h-16'
                    }`}
                  />
                )
              }

              const displayDate = cell.date.format('D')
              const dayType = cell.item?.dayType
              return (
                <button
                  key={cell.date.format('YYYY-MM-DD')}
                  type="button"
                  className={`group flex flex-col rounded-xl border px-2 py-1 text-left transition-colors ${
                    cell.item
                      ? dayType === 'HOLIDAY'
                        ? 'border-red-200 bg-red-50/70 hover:bg-red-100'
                        : 'border-green-200 bg-green-50/70 hover:bg-green-100'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  } ${compact ? 'h-12' : 'h-16'}`}
                  onClick={() => {
                    if (cell.item && onEditItem) {
                      onEditItem(cell.item)
                      return
                    }
                    if (!cell.item && cell.date && onAddDate) {
                      onAddDate(cell.date)
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-1">
                    <span className="text-[13px] font-semibold text-slate-900">{displayDate}</span>
                    <span className="text-[10px] text-slate-400">
                      {cell.item ? (dayType === 'HOLIDAY' ? '假' : '补') : '+'}
                    </span>
                  </div>
                  <div className="mt-1 truncate text-[11px] leading-4 text-slate-600">
                    {cell.item ? cell.item.name : '添加'}
                  </div>
                </button>
              )
            }),
          )}
        </div>
      </div>
    </Card>
  )
}

function buildImportPreview(payload: HolidayImportPayload, year: number) {
  if (!Array.isArray(payload.days)) {
    throw new Error('导入内容必须包含 days 数组')
  }

  if (payload.days.length === 0) {
    throw new Error('days 数组不能为空')
  }

  const seenDates = new Set<string>()

  const items = payload.days.map<HolidayImportPreviewItem>((item, index) => {
    if (!item || typeof item !== 'object') {
      throw new Error(`第 ${index + 1} 项格式非法`)
    }

    const date = String(item.date ?? '').trim()
    const name = String(item.name ?? '').trim()
    const isOffDay = item.isOffDay

    if (!date || !name) {
      throw new Error(`第 ${index + 1} 项缺少日期或名称`)
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !dayjs(date).isValid()) {
      throw new Error(`第 ${index + 1} 项日期格式非法`)
    }

    if (typeof isOffDay !== 'boolean') {
      throw new Error(`第 ${index + 1} 项 isOffDay 必须为布尔值`)
    }

    if (dayjs(date).year() !== year) {
      throw new Error(`日期 ${date} 不属于当前年份`)
    }

    if (seenDates.has(date)) {
      throw new Error(`导入数据中存在重复日期: ${date}`)
    }
    seenDates.add(date)

    return {
      holidayDate: date,
      name,
      dayType: isOffDay ? 'HOLIDAY' : 'MAKEUP_DAY',
    }
  })

  return sortItems(items)
}

function buildDateRange(startDate: Dayjs, endDate: Dayjs) {
  const dates: string[] = []
  let cursor = startDate.startOf('day')
  const normalizedEnd = endDate.startOf('day')

  while (cursor.isSame(normalizedEnd, 'day') || cursor.isBefore(normalizedEnd, 'day')) {
    dates.push(padDate(cursor))
    cursor = cursor.add(1, 'day')
  }

  return dates
}

const HolidayManagementPage = () => {
  const [selectedYear, setSelectedYear] = useState(dayjs().year())
  const [loading, setLoading] = useState(false)
  const [mutating, setMutating] = useState(false)
  const [items, setItems] = useState<HolidayItem[]>([])
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [calendarTab, setCalendarTab] = useState<'year' | 'month'>('year')
  const [selectedCalendarMonth, setSelectedCalendarMonth] = useState(dayjs().month())

  const [addOpen, setAddOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<HolidayItem | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState('')
  const [importPreview, setImportPreview] = useState<HolidayImportPreviewItem[]>([])
  const [importPreviewError, setImportPreviewError] = useState('')

  const [addForm] = Form.useForm<AddHolidayFormValues>()
  const [editForm] = Form.useForm<EditHolidayFormValues>()

  const yearRange = useMemo(() => {
    const start = dayjs().year(selectedYear).startOf('year')
    const end = dayjs().year(selectedYear).endOf('year')
    return { start, end }
  }, [selectedYear])

  const yearCount = items.length
  const holidayCount = useMemo(() => items.filter((item) => item.dayType === 'HOLIDAY').length, [items])
  const makeupCount = yearCount - holidayCount

  const monthlyItems = useMemo(() => getMonthItems(items, selectedCalendarMonth), [items, selectedCalendarMonth])
  const columns = useMemo(
    () => buildCalendarColumns(openEditModal, handleDeleteItem),
    [handleDeleteItem, openEditModal],
  )

  const importPreviewConflictDates = useMemo(() => {
    const currentDates = new Set(items.map((item) => item.holidayDate))
    return importPreview
      .filter((item) => currentDates.has(item.holidayDate))
      .map((item) => item.holidayDate)
  }, [importPreview, items])

  const loadYear = async (year: number) => {
    try {
      setLoading(true)
      const response = await holidayApi.getYear(year)
      const normalized = sortItems(
        response.map((item) => ({
          id: item.id,
          holidayDate: item.holidayDate,
          name: item.name,
          dayType: item.dayType,
        })),
      )

      setSelectedYear(year)
      setItems(normalized)
      setSelectedCalendarMonth(dayjs().year(year).month())
    } catch (error) {
      Toast.error('加载节假日数据失败')
      console.error('加载节假日数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadYear(selectedYear)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function resetAddForm(date?: Dayjs) {
    addForm.setFieldsValue({
      dateRange: date ? [date, date] : undefined,
      name: '',
      dayType: 'HOLIDAY',
    })
  }

  function resetEditForm(item: HolidayItem) {
    editForm.setFieldsValue({
      holidayDate: dayjs(item.holidayDate),
      name: item.name,
      dayType: item.dayType,
    })
  }

  function openAddModal(date?: Dayjs) {
    resetAddForm(date)
    setAddOpen(true)
  }

  function closeAddModal() {
    setAddOpen(false)
    addForm.resetFields()
  }

  function openEditModal(item: HolidayItem) {
    setEditingItem(item)
    resetEditForm(item)
    setEditOpen(true)
  }

  function closeEditModal() {
    setEditOpen(false)
    setEditingItem(null)
    editForm.resetFields()
  }

  function closeImportModal() {
    setImportOpen(false)
    setImportText('')
    setImportPreview([])
    setImportPreviewError('')
  }

  async function refreshCurrentYear() {
    await loadYear(selectedYear)
  }

  async function handleDeleteItem(item: HolidayItem) {
    if (!item.id) {
      Toast.error('缺少可删除的节假日ID')
      return
    }

    Modal.confirm({
      title: '确认删除',
      content: `确定删除 ${item.holidayDate} 的 ${item.name} 吗？`,
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setMutating(true)
          await holidayApi.batchDelete([Number(item.id)])
          Toast.success('删除成功')
          await refreshCurrentYear()
        } catch (error) {
          Toast.error('删除失败')
          console.error('删除节假日失败:', error)
        } finally {
          setMutating(false)
        }
      },
    })
  }

  async function handleClearYear() {
    if (items.length === 0) {
      Toast.info('当前年份没有可清空的节假日')
      return
    }

    const ids = items
      .map((item) => Number(item.id))
      .filter((id) => Number.isFinite(id))

    if (ids.length === 0) {
      Toast.error('当前年份数据缺少可删除ID')
      return
    }

    Modal.confirm({
      title: '确认清空',
      content: `将清空 ${selectedYear} 年的全部节假日和补班配置，是否继续？`,
      okText: '确认清空',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setMutating(true)
          await holidayApi.batchDelete(ids)
          Toast.success('清空成功')
          await refreshCurrentYear()
        } catch (error) {
          Toast.error('清空失败')
          console.error('清空当前年份失败:', error)
        } finally {
          setMutating(false)
        }
      },
    })
  }

  async function handleSubmitAdd() {
    let submitted = false
    try {
      const values = await addForm.validateFields()
      const [startDate, endDate] = values.dateRange

      if (startDate.year() !== selectedYear || endDate.year() !== selectedYear) {
        Toast.warning('新增日期必须属于当前年份')
        return
      }

      if (endDate.isBefore(startDate, 'day')) {
        Toast.warning('结束日期不能早于开始日期')
        return
      }

      const dateList = buildDateRange(startDate, endDate)
      const conflictDates = dateList.filter((date) => items.some((item) => item.holidayDate === date))

      if (conflictDates.length > 0) {
        Toast.warning(`存在已配置日期：${conflictDates.join('、')}`)
        return
      }

      setMutating(true)
      submitted = true
      for (const holidayDate of dateList) {
        await holidayApi.create({
          holidayDate,
          name: values.name.trim(),
          dayType: values.dayType,
        })
      }

      Toast.success('新增成功')
      closeAddModal()
      await refreshCurrentYear()
    } catch (error) {
      if (submitted) {
        await refreshCurrentYear()
      }
      if (error instanceof Error) {
        Toast.error(error.message)
      }
    } finally {
      setMutating(false)
    }
  }

  async function handleSubmitEdit() {
    if (!editingItem?.id) {
      Toast.error('缺少可编辑的节假日ID')
      return
    }

    try {
      const values = await editForm.validateFields()
      const holidayDate = padDate(values.holidayDate)

      if (dayjs(holidayDate).year() !== selectedYear) {
        Toast.warning('节假日日期必须属于当前年份')
        return
      }

      const conflict = items.find(
        (item) => item.holidayDate === holidayDate && Number(item.id) !== Number(editingItem.id),
      )
      if (conflict) {
        Toast.warning(`该日期已存在配置：${holidayDate}`)
        return
      }

      setMutating(true)
      await holidayApi.update(Number(editingItem.id), {
        holidayDate,
        name: values.name.trim(),
        dayType: values.dayType,
      })
      Toast.success('更新成功')
      closeEditModal()
      await refreshCurrentYear()
    } catch (error) {
      if (error instanceof Error) {
        Toast.error(error.message)
      }
    } finally {
      setMutating(false)
    }
  }

  function parseImportText() {
    const parsed = JSON.parse(importText) as HolidayImportPayload
    const preview = buildImportPreview(parsed, selectedYear)
    setImportPreview(preview)
    setImportPreviewError('')
  }

  async function handleConfirmImport() {
    if (importPreview.length === 0) {
      parseImportText()
      return
    }

    if (importPreviewConflictDates.length > 0) {
      Toast.warning(`预览中存在已配置日期：${importPreviewConflictDates.join('、')}`)
      return
    }

    let submitted = false
    try {
      setMutating(true)
      submitted = true
      for (const item of importPreview) {
        await holidayApi.create({
          holidayDate: item.holidayDate,
          name: item.name.trim(),
          dayType: item.dayType,
        })
      }

      Toast.success('导入成功')
      closeImportModal()
      await refreshCurrentYear()
    } catch (error) {
      if (submitted) {
        await refreshCurrentYear()
      }
      Toast.error(error instanceof Error ? error.message : '导入失败')
      console.error('导入节假日失败:', error)
    } finally {
      setMutating(false)
    }
  }

  async function handleImportAction() {
    if (importPreview.length === 0) {
      try {
        parseImportText()
      } catch (error) {
        setImportPreview([])
        setImportPreviewError(error instanceof Error ? error.message : '导入失败')
      }
      return
    }

    await handleConfirmImport()
  }

  return (
    <div className="p-6">
      <Card
        className="rounded-2xl border-0 shadow-[0_18px_40px_-30px_rgba(15,23,42,0.32)]"
        title="节假日管理"
        extra={
          <Space wrap>
            <DatePicker
              picker="year"
              allowClear={false}
              inputReadOnly
              value={dayjs().year(selectedYear)}
              format="YYYY"
              onChange={(value) => {
                if (!value) return
                void loadYear(value.year())
              }}
            />
            <Button icon={<CalendarOutlined />} onClick={() => setCalendarOpen(true)}>
              查看日历
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => openAddModal()}>
              新增节假日
            </Button>
            <Button icon={<ImportOutlined />} onClick={() => setImportOpen(true)}>
              解析导入
            </Button>
            <Button danger icon={<DeleteOutlined />} loading={mutating} onClick={handleClearYear}>
              清空当前年份
            </Button>
          </Space>
        }
      >
        <div className="mb-4 grid gap-3 md:grid-cols-3">
          <Card size="small" className="bg-slate-50">
            <div className="text-sm text-slate-500">当前年份</div>
            <div className="mt-1 text-2xl font-semibold text-slate-900">{selectedYear}</div>
          </Card>
          <Card size="small" className="bg-slate-50">
            <div className="text-sm text-slate-500">总配置数</div>
            <div className="mt-1 text-2xl font-semibold text-slate-900">{yearCount}</div>
          </Card>
          <Card size="small" className="bg-slate-50">
            <div className="text-sm text-slate-500">类型分布</div>
            <div className="mt-1 flex flex-wrap gap-2">
              <Tag color="red">假期 {holidayCount}</Tag>
              <Tag color="green">补班 {makeupCount}</Tag>
            </div>
          </Card>
        </div>

        {items.length > 0 ? (
          <Table
            rowKey={(record) => record.id ?? record.holidayDate}
            columns={columns}
            dataSource={items}
            loading={loading}
            pagination={false}
          />
        ) : (
          <Empty description="当前年份暂无节假日配置" />
        )}
      </Card>

      <Modal
        title="新增节假日"
        open={addOpen}
        onCancel={closeAddModal}
        onOk={handleSubmitAdd}
        okText="确认新增"
        confirmLoading={mutating}
        destroyOnClose
        width={720}
      >
        <Form layout="vertical" form={addForm} preserve={false}>
          <Form.Item
            label="日期区间"
            name="dateRange"
            rules={[{ required: true, message: '请选择日期区间' }]}
          >
            <RangePicker
              style={{ width: '100%' }}
              inputReadOnly
              disabledDate={(current) => {
                if (!current) return true
                return current.isBefore(yearRange.start, 'day') || current.isAfter(yearRange.end, 'day')
              }}
            />
          </Form.Item>
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请输入节假日名称' }]}
          >
            <Input placeholder="例如：春节" />
          </Form.Item>
          <Form.Item label="类型" name="dayType" initialValue="HOLIDAY" rules={[{ required: true }]}>
            <Select options={DAY_TYPE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑节假日"
        open={editOpen}
        onCancel={closeEditModal}
        onOk={handleSubmitEdit}
        okText="保存"
        confirmLoading={mutating}
        destroyOnClose
      >
        <Form layout="vertical" form={editForm} preserve={false}>
          <Form.Item
            label="日期"
            name="holidayDate"
            rules={[{ required: true, message: '请选择日期' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              inputReadOnly
              disabledDate={(current) => {
                if (!current) return true
                return current.isBefore(yearRange.start, 'day') || current.isAfter(yearRange.end, 'day')
              }}
            />
          </Form.Item>
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请输入节假日名称' }]}
          >
            <Input placeholder="例如：春节" />
          </Form.Item>
          <Form.Item label="类型" name="dayType" rules={[{ required: true }]}>
            <Select options={DAY_TYPE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="解析导入"
        open={importOpen}
        onCancel={closeImportModal}
        onOk={handleImportAction}
        okText={importPreview.length > 0 ? '确认新增' : '解析预览'}
        confirmLoading={mutating}
        destroyOnClose
        width={920}
      >
        <Space direction="vertical" className="mb-3 w-full" size={12}>
          <Text type="secondary">
            请输入包含 `days` 数组的 JSON 对象，数组项需包含 `name`、`date`、`isOffDay`。
          </Text>
          <TextArea
            rows={12}
            value={importText}
            onChange={(event) => {
              setImportText(event.target.value)
              setImportPreview([])
              setImportPreviewError('')
            }}
            placeholder={`{
  "year": 2026,
  "days": [
    { "name": "元旦", "date": "2026-01-01", "isOffDay": true }
  ]
}`}
          />

          {importPreviewError ? (
            <Alert type="error" showIcon message={importPreviewError} />
          ) : null}

          {importPreview.length > 0 ? (
            <Space direction="vertical" className="w-full" size={12}>
              <div className="flex items-center justify-between">
                <Text strong>预览新增内容</Text>
                <Text type="secondary">共 {importPreview.length} 条</Text>
              </div>
              {importPreviewConflictDates.length > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message={`以下日期已存在配置，请先移除或修改后再新增：${importPreviewConflictDates.join('、')}`}
                />
              ) : null}
              <Table
                rowKey={(record) => record.holidayDate}
                columns={[
                  {
                    title: '日期',
                    dataIndex: 'holidayDate',
                    width: 140,
                    render: (value: string) => toDisplayDate(value),
                  },
                  {
                    title: '节假日名称',
                    dataIndex: 'name',
                    render: (value: string) => <span className="font-medium text-slate-900">{value}</span>,
                  },
                  {
                    title: '类型',
                    dataIndex: 'dayType',
                    width: 110,
                    render: (value: HolidayDayType) => (
                      <Tag color={getDayTypeColor(value)}>{getDayTypeText(value)}</Tag>
                    ),
                  },
                ]}
                dataSource={importPreview}
                pagination={false}
                size="small"
              />
            </Space>
          ) : null}
        </Space>
      </Modal>

      <Modal
        title="年度日历"
        open={calendarOpen}
        onCancel={() => setCalendarOpen(false)}
        footer={null}
        width={1280}
      >
        <Tabs
          activeKey={calendarTab}
          onChange={(key) => setCalendarTab(key as 'year' | 'month')}
          items={[
            {
              key: 'year',
              label: '按年',
              children: (
                <div className="max-h-[70vh] overflow-y-auto pr-1">
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {MONTH_NAMES.map((_monthName, index) => {
                      const monthItems = getMonthItems(items, index)
                      return (
                        <MonthCalendarCard
                          key={_monthName}
                          year={selectedYear}
                          monthIndex={index}
                          items={monthItems}
                          compact
                          onAddDate={(date) => openAddModal(date)}
                          onEditItem={openEditModal}
                        />
                      )
                    })}
                  </div>
                </div>
              ),
            },
            {
              key: 'month',
              label: '按月',
              children: (
                <div className="space-y-4">
                  <Select
                    value={selectedCalendarMonth}
                    style={{ width: 180 }}
                    options={MONTH_NAMES.map((monthName, index) => ({
                      label: monthName,
                      value: index,
                    }))}
                    onChange={(value) => setSelectedCalendarMonth(value)}
                  />
                  <MonthCalendarCard
                    year={selectedYear}
                    monthIndex={selectedCalendarMonth}
                    items={monthlyItems}
                    onAddDate={(date) => openAddModal(date)}
                    onEditItem={openEditModal}
                  />
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  )
}

export default HolidayManagementPage
